import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { MapModel, ResultGoogle, zipCodeValidator } from './model/map-model';
import { SesionService } from 'src/app/core/service/sesion.service';
import { PotentialCustomer } from 'src/app/pages/potential-customer/model/potential-customer';
import { debounceTime, distinctUntilChanged, Observable } from 'rxjs';
import { MapService } from './service/map.service';
import { GoogleMap } from '@angular/google-maps';
import { ShadowScrollService } from 'src/app/core/service/utils/shadow-scroll.service';

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: MapComponent,
      multi: true,
    },
  ],
})
export class MapComponent implements OnInit, ControlValueAccessor {
  @Input()
  required: boolean;

  @Input()
  submitted: boolean;
  mapForm: FormGroup;
  sesionData: PotentialCustomer | null;
  location!: MapModel;
  showStateWarning: boolean;
  expression: RegExp = /^[A-Za-záéíóúüñÁÉÍÓÚÜÑ\d\s]+\s\d+,\s[A-Za-záéíóúüñÁÉÍÓÚÜÑ\d\s]+,\s[A-Za-záéíóúüñÁÉÍÓÚÜÑ\d\s]+$/;
  isCABA: boolean = false;
  isBSAS: boolean = false;
  directionWarning: boolean = false;
  editingMarker: boolean = false;

  @ViewChild(GoogleMap, { static: false }) map!: GoogleMap;
  mapOptions: any = {
    center: { lat: -25.30066, lng: -57.63591 },
    zoom: 17,
    mapTypeControlOptions: { mapTypeIds: [] },
    disableDefaultUI: true,
    mapTypeControl: true,
    scaleControl: true,
    mapTypeId: 'roadmap',
    clickableIcons: false,
    draggableCursor: 'crosshair',
  };
  zoom: number;
  markerPosition: any = null;
  apiLoaded: Observable<boolean>;
  showErrorConfirm: boolean = false;

  onChange!: (value: any) => void;

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly fb: FormBuilder,
    private readonly sesion: SesionService,
    private readonly mapService: MapService,
    public shadowScroll: ShadowScrollService,
  ) {
    this.zoom = 17;
    this.apiLoaded = mapService.googleMapsDinamicApi();
    this.submitted = false;
    this.required = false;
    this.showStateWarning = false;
    this.sesionData = this.sesion.getItem('potential_customer');
    this.mapForm = this.fb.group({
      locationStreet: [
        this.sesionData ? this.sesionData.location.street : '',
        [Validators.required, Validators.maxLength(60)],
      ],
      locationStreetNumber: [
        this.sesionData ? this.sesionData.location.streetNumber : '',
        [Validators.maxLength(10), Validators.required],
      ],
      locationCity: [this.sesionData ? this.sesionData.location.city : '', Validators.required],
      locationState: [this.sesionData ? this.sesionData.location.state : '', Validators.required],
      entreCalles: [this.sesionData ? this.sesionData.location.entreCalles : '', Validators.maxLength(40)],
      depto: [this.sesionData ? this.sesionData.location.depto : '', Validators.maxLength(10)],
      zipCode: [
        this.sesionData ? this.sesionData.location.postCode : '',
        [Validators.pattern(/^\d{5}-\d{3}$/), zipCodeValidator(), Validators.required],
      ],
    });
    this.detectChangeForm();
    this.mapForm.controls['zipCode'].valueChanges.subscribe((value) => {
      if (this.location && value != '') {
        this.location.postCode = value;
        this.dispatchChange();
      }
    });
  }

  areAllControlsValid(controlNames: string[]): boolean {
    return controlNames.every((controlName) => this.mapForm.controls[controlName].valid);
  }

  ngOnInit(): void {
    this.formDetectChanges();
    if (this.sesionData) {
      this.validateDirec();
    }
  }

  detectChangeForm() {
    const formControls = ['locationCity', 'locationStreet', 'locationState', 'locationStreetNumber', 'zipCode'];
    formControls.forEach((controlName) => {
      this.mapForm.controls[controlName].valueChanges
        .pipe(debounceTime(600), distinctUntilChanged())
        .subscribe((value) => {
          if (this.areAllControlsValid(formControls)) {
            this.validateDirec();
          } else {
            this.onChange(null);
          }
        });
    });
  }

  validateDirec(coordinates?: { lat: number; lng: number }) {
    let street = this.mapForm.controls['locationStreet'].value;
    let state = this.mapForm.controls['locationState'].value;
    let city = this.mapForm.controls['locationCity'].value;
    let direction = street + ', ' + city + ', ' + state + ', brasil';
    this.parseDirec(direction, coordinates);
    this.directionWarning = false;
  }

  parseDirec(direccion: string, coordinates?: { lat: number; lng: number }) {
    let direcParse = this.replaceSpacesWithPlus(direccion);

    this.mapService.getDirection(direcParse).subscribe((data) => {
      if (data.status == 'OK') {
        let street: boolean = false;
        let postalCode: boolean = false;
        let resultTypeStreetNumber = data.results[0].address_components.find((result: any) =>
          result.types.includes('street_number'),
        );
        if (resultTypeStreetNumber) {
          street = true;
        }
        let resultTypePostalCode = data.results[0].address_components.find((result: any) =>
          result.types.includes('postal_code'),
        );
        if (resultTypePostalCode) {
          postalCode = true;
        }
        if (!coordinates) {
          this.markerPosition = {
            lat: data.results[0].geometry.location.lat,
            lng: data.results[0].geometry.location.lng,
          };
          this.mapOptions.center = this.markerPosition;
          this.zoom = 17;
          if (this.map) {
            this.map.panTo(this.markerPosition);
          }
        }
        if (!street || !postalCode) {
          this.mapService
            .getDirectionWithCordinates(data.results[0].geometry.location.lat, data.results[0].geometry.location.lng)
            .subscribe((dataTwo) => {
              let postCode: string = '';
              for (let result of dataTwo.results[0].address_components) {
                if (result.types.includes('postal_code')) postCode = result.long_name;
              }
              this.parseResultMap(data, postCode, coordinates);
            });
        } else {
          this.parseResultMap(data, undefined, coordinates);
        }
      } else {
        this.onChange(null);
      }
    });
    this.cd.detectChanges();
  }

  getAutomaticLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        console.log('position', position);
        this.setDirectionWithCordinates(position.coords.latitude, position.coords.longitude);
      });
    }
  }

  setDirectionWithCordinates(lat: number, lng: number) {
    this.markerPosition = {
      lat: lat,
      lng: lng,
    };
    this.mapOptions.center = this.markerPosition;
    if (this.map) {
      this.map.panTo(this.markerPosition);
      this.cd.detectChanges();
    }
    this.mapService.getDirectionWithCordinates(lat, lng).subscribe((dataTwo) => {
      let googleResult = this.mapResultGoogle(dataTwo);
      this.changueInputWithNewlocation(googleResult);
      this.parseResultMap(dataTwo, googleResult.postalCode, this.markerPosition);
    });
  }

  changueInputWithNewlocation(data: any) {
    this.mapForm.controls['locationStreet'].setValue(data.route);
    this.mapForm.controls['locationCity'].setValue(data.locality);
    this.mapForm.controls['locationState'].setValue(data.state);
    console.log(data.postalCode);
    console.log(this.mapForm);
  }

  mapResultGoogle(data: ResultGoogle) {
    let response = {
      streetNumber: '',
      route: '',
      locality: '',
      country: '',
      postalCode: '',
      state: '',
    };
    for (let result of data.results[0].address_components) {
      switch (true) {
        case result.types.includes('street_number'):
          response.streetNumber = result.long_name;
          break;
        case result.types.includes('route'):
          response.route = result.long_name;
          break;
        case result.types.includes('locality'):
          response.locality = result.long_name;
          break;
        case result.types.includes('administrative_area_level_1'):
          response.state = result.long_name;
          break;
        case result.types.includes('administrative_area_level_2'):
          if (!response.state) response.state = result.long_name;
          break;
        case result.types.includes('postal_code'):
          response.postalCode = result.long_name;
          break;
        default:
          break;
      }
    }
    return response;
  }

  parseResultMap(data: ResultGoogle, postCode?: string, coordinates?: { lat: number; lng: number }) {
    let resulGoogle = this.mapResultGoogle(data);
    let locationDto: MapModel = {
      address: data.results[0].formatted_address,
      street: this.mapForm.controls['locationStreet'].value,
      streetNumber: this.mapForm.controls['locationStreetNumber'].value,
      state: this.removeProvincia(resulGoogle.state),
      city: resulGoogle.locality ? resulGoogle.locality : this.mapForm.controls['locationCity'].value,
      entreCalles: this.mapForm.controls['entreCalles'].value,
      depto: this.mapForm.controls['depto'].value,
      postCode: this.mapForm.controls['zipCode'].value,
      coordinates: {
        lon: coordinates ? coordinates.lng : data.results[0].geometry.location.lng,
        lat: coordinates ? coordinates.lat : data.results[0].geometry.location.lat,
      },
    };
    this.location = locationDto;
    this.dispatchChange();
  }

  removeProvincia(text: string): string {
    return text.replace(/Provincia de /g, '');
  }

  writeValue(obj: any): void {
    this.location = obj;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState?(isDisabled: boolean): void {}

  dispatchChange() {
    if (this.mapForm.valid) {
      this.onChange(this.location);
    } else {
      this.cd.detectChanges();
      this.onChange(null);
    }
  }

  replaceSpacesWithPlus(text: string): string {
    return text.replace(/\s+/g, '+');
  }

  removeCommas(text: string): string {
    return text.replace(/,/g, '');
  }

  formDetectChanges() {
    this.mapForm.controls['entreCalles'].valueChanges.subscribe((val) => {
      if (this.location) {
        this.location.entreCalles = this.mapForm.controls['entreCalles'].value;
        this.dispatchChange();
      }
    });
    this.mapForm.controls['depto'].valueChanges.subscribe((val) => {
      if (this.location) {
        this.location.depto = this.mapForm.controls['depto'].value;
        this.dispatchChange();
      }
    });
  }

  zommChangedMap() {
    this.zoom = this.map.getZoom() ?? 17;
  }

  @HostListener('scroll', ['$event'])
  onScroll(e: any) {
    this.shadowScroll.scroll(e);
  }

  confirm() {
    if (!this.mapForm.get('locationStreet')?.valid) {
      this.showErrorConfirm = true;
      return;
    }
    this.location.street = this.mapForm.get('locationStreet')?.value;
    this.dispatchChange();
    this.showErrorConfirm = false;
    this.editingMarker = false;
  }
}
